// src/pages/VerificarIntegradoPage.tsx
import { useState } from 'react';
import { Shield, CheckCircle, XCircle, Search, FileText, User, Calendar, DollarSign, Loader2 } from 'lucide-react';
import api from '../services/api';

interface DocumentoVerificado {
  numero: string;
  fecha: string;
  total: number;
  estado: string;
  cliente: string;
  vendedor: string;
  codigo_verificacion: string;
}

interface VerificacionResponse {
  valido: boolean;
  mensaje: string;
  codigo?: string;
  documento?: DocumentoVerificado;
}

export function VerificarIntegradoPage() {
  const [codigo, setCodigo] = useState('');
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState<VerificacionResponse | null>(null);
  const [verificado, setVerificado] = useState(false);

  const verificarCodigo = async (codigoAVerificar: string) => {
    if (!codigoAVerificar.trim()) return;
    
    setLoading(true);
    setVerificado(false);
    setResultado(null);
    
    try {
      const response = await api.get<VerificacionResponse>(`/notas-venta/verificar/${codigoAVerificar.toUpperCase()}`);
      setResultado(response.data);
      setVerificado(true);
    } catch (error: any) {
      setResultado({
        valido: false,
        mensaje: error.response?.data?.mensaje || 'Error al verificar el código',
        codigo: codigoAVerificar
      });
      setVerificado(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    verificarCodigo(codigo);
  };

  const formatFecha = (fecha: string) => {
    return new Date(fecha).toLocaleDateString('es-EC', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="bg-blue-500 p-3 rounded-xl">
          <Shield className="w-8 h-8 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Verificar Documento</h1>
          <p className="text-gray-500">Valida la autenticidad de notas de venta</p>
        </div>
      </div>

      {/* Formulario de búsqueda */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <form onSubmit={handleSubmit} className="flex gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value.toUpperCase())}
              placeholder="Ingrese código de verificación (ej: AB12CD34)"
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-lg tracking-widest"
              maxLength={8}
            />
          </div>
          <button
            type="submit"
            disabled={loading || !codigo.trim()}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors flex items-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Verificando...
              </>
            ) : (
              <>
                <Shield className="w-5 h-5" />
                Verificar
              </>
            )}
          </button>
        </form>
      </div>

      {/* Resultado de verificación */}
      {verificado && resultado && (
        <div className={`bg-white rounded-xl shadow-sm overflow-hidden border-2 ${
          resultado.valido ? 'border-green-500' : 'border-red-500'
        }`}>
          {/* Header del resultado */}
          <div className={`px-6 py-4 ${resultado.valido ? 'bg-green-50' : 'bg-red-50'}`}>
            <div className="flex items-center gap-3">
              {resultado.valido ? (
                <CheckCircle className="w-8 h-8 text-green-500" />
              ) : (
                <XCircle className="w-8 h-8 text-red-500" />
              )}
              <div>
                <h3 className={`text-xl font-bold ${resultado.valido ? 'text-green-700' : 'text-red-700'}`}>
                  {resultado.valido ? '✓ DOCUMENTO AUTÉNTICO' : '✗ DOCUMENTO NO VÁLIDO'}
                </h3>
                <p className="text-gray-600">{resultado.mensaje}</p>
              </div>
            </div>
          </div>

          {/* Detalles del documento */}
          {resultado.valido && resultado.documento && (
            <div className="p-6">
              <div className="grid gap-4">
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                  <FileText className="w-6 h-6 text-blue-500" />
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium">Número de Documento</p>
                    <p className="text-lg font-bold text-gray-800">{resultado.documento.numero}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                    <Calendar className="w-6 h-6 text-purple-500" />
                    <div>
                      <p className="text-xs text-gray-500 uppercase font-medium">Fecha de Emisión</p>
                      <p className="font-semibold text-gray-800">{formatFecha(resultado.documento.fecha)}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                    <DollarSign className="w-6 h-6 text-green-500" />
                    <div>
                      <p className="text-xs text-gray-500 uppercase font-medium">Total</p>
                      <p className="font-bold text-lg text-gray-800">${resultado.documento.total.toFixed(2)}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                    <User className="w-6 h-6 text-cyan-500" />
                    <div>
                      <p className="text-xs text-gray-500 uppercase font-medium">Cliente</p>
                      <p className="font-semibold text-gray-800">{resultado.documento.cliente}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                    <User className="w-6 h-6 text-amber-500" />
                    <div>
                      <p className="text-xs text-gray-500 uppercase font-medium">Vendedor</p>
                      <p className="font-semibold text-gray-800">{resultado.documento.vendedor}</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium">Estado del Documento</p>
                    <p className={`font-bold ${resultado.documento.estado === 'ACTIVA' ? 'text-green-600' : 'text-red-600'}`}>
                      {resultado.documento.estado === 'ACTIVA' ? '✓ ACTIVO' : '✗ ANULADO'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500 uppercase font-medium">Código Verificado</p>
                    <p className="font-mono text-lg font-bold text-blue-600">
                      {resultado.documento.codigo_verificacion}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Si no es válido */}
          {!resultado.valido && (
            <div className="p-6 text-center text-gray-600">
              <p className="mb-2">El código <span className="font-mono text-red-600 font-bold">{resultado.codigo}</span> no corresponde a ningún documento.</p>
              <p className="text-sm">Verifique que el código esté escrito correctamente.</p>
            </div>
          )}
        </div>
      )}

      {/* Info */}
      <div className="bg-blue-50 rounded-xl p-4 text-center text-sm text-blue-700">
        <p>🔒 Los documentos de BIO'DACAR contienen un código único de 8 caracteres para verificar su autenticidad.</p>
      </div>
    </div>
  );
}
